import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useCallback } from 'react';
import { formatCurrency } from '@/utils/formatting';
const LINE_COLORS = [
    '#38bdf8',
    '#a78bfa',
    '#fbbf24',
    '#f472b6',
    '#2dd4bf',
    '#fb923c',
];
const TOTAL_COLOR = '#34d399';
export const EquityCurve = React.memo(function EquityCurve({ experiments }) {
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const { series, allDates, yMin, yMax, totalSeries } = useMemo(() => {
        const seriesList = [];
        const dateSet = new Set();
        experiments.forEach((exp, idx) => {
            const stats = exp.daily_stats;
            if (!stats || Object.keys(stats).length === 0)
                return;
            const points = [];
            const sortedDates = Object.keys(stats).sort();
            for (const d of sortedDates) {
                const entry = stats[d];
                if (!entry)
                    continue;
                dateSet.add(d);
                points.push({
                    date: d,
                    value: entry.ending_balance,
                });
            }
            if (points.length > 0) {
                seriesList.push({
                    id: exp.experiment_id,
                    name: exp.experiment_name,
                    color: LINE_COLORS[idx % LINE_COLORS.length] ?? '#94a3b8',
                    points,
                });
            }
        });
        const allDates = Array.from(dateSet).sort();
        const totalPoints = [];
        for (const d of allDates) {
            let total = 0;
            let hasData = false;
            for (const exp of experiments) {
                const stats = exp.daily_stats;
                const dayEntry = stats ? stats[d] : undefined;
                if (dayEntry) {
                    total += dayEntry.ending_balance;
                    hasData = true;
                }
                else if (stats) {
                    const sortedExpDates = Object.keys(stats).sort();
                    const earlier = sortedExpDates.filter(ed => ed <= d);
                    const lastDate = earlier[earlier.length - 1];
                    const fallbackEntry = lastDate ? stats[lastDate] : undefined;
                    if (fallbackEntry) {
                        total += fallbackEntry.ending_balance;
                        hasData = true;
                    }
                }
            }
            if (hasData) {
                totalPoints.push({ date: d, value: total });
            }
        }
        const totalSeries = {
            id: '__total__',
            name: 'Combined Total',
            color: TOTAL_COLOR,
            points: totalPoints,
        };
        const allValues = [
            ...seriesList.flatMap(s => s.points.map(p => p.value)),
            ...totalPoints.map(p => p.value),
        ];
        const yMin = allValues.length > 0 ? Math.min(...allValues) : 0;
        const yMax = allValues.length > 0 ? Math.max(...allValues) : 1000;
        return { series: seriesList, allDates, yMin, yMax, totalSeries };
    }, [experiments]);
    const width = 800;
    const height = 300;
    const padding = { top: 20, right: 20, bottom: 30, left: 60 };
    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;
    const xScale = useCallback((dateStr) => {
        if (allDates.length <= 1)
            return padding.left + chartW / 2;
        const idx = allDates.indexOf(dateStr);
        return padding.left + (idx / (allDates.length - 1)) * chartW;
    }, [allDates, chartW, padding.left]);
    const yPad = Math.max((yMax - yMin) * 0.1, 10);
    const yDomain = { min: yMin - yPad, max: yMax + yPad };
    const yScale = useCallback((value) => {
        const range = yDomain.max - yDomain.min;
        if (range === 0)
            return padding.top + chartH / 2;
        return padding.top + chartH - ((value - yDomain.min) / range) * chartH;
    }, [yDomain, chartH, padding.top]);
    const buildPolyline = useCallback((points) => {
        return points
            .map(p => `${xScale(p.date)},${yScale(p.value)}`)
            .join(' ');
    }, [xScale, yScale]);
    const handleMouseMove = useCallback((e) => {
        const svg = e.currentTarget;
        const rect = svg.getBoundingClientRect();
        const mouseX = ((e.clientX - rect.left) / rect.width) * width;
        if (allDates.length === 0)
            return;
        let nearestIdx = 0;
        let nearestDist = Infinity;
        for (let i = 0; i < allDates.length; i++) {
            const dateStr = allDates[i];
            const x = xScale(dateStr);
            const dist = Math.abs(mouseX - x);
            if (dist < nearestDist) {
                nearestDist = dist;
                nearestIdx = i;
            }
        }
        if (nearestDist > chartW / allDates.length) {
            setHoveredPoint(null);
            return;
        }
        const date = allDates[nearestIdx];
        const x = xScale(date);
        const values = [];
        const totalPt = totalSeries.points.find(p => p.date === date);
        if (totalPt) {
            values.push({ name: 'Combined', value: totalPt.value, color: TOTAL_COLOR });
        }
        for (const s of series) {
            const pt = s.points.find(p => p.date === date);
            if (pt) {
                values.push({ name: s.name, value: pt.value, color: s.color });
            }
        }
        setHoveredPoint({ x, y: padding.top, date, values });
    }, [allDates, series, totalSeries, xScale, chartW, padding.top]);
    const handleMouseLeave = useCallback(() => {
        setHoveredPoint(null);
    }, []);
    if (series.length === 0 || allDates.length < 2) {
        return (_jsxs("div", { className: "bg-surface-dark border border-surface-light rounded-xl overflow-hidden shadow-sm mb-6", children: [_jsx("div", { className: "p-5 border-b border-surface-light", children: _jsxs("h3", { className: "font-bold text-white text-lg flex items-center gap-2", children: [_jsx("span", { className: "text-success", children: "\uD83D\uDCC8" }), "Equity Curve"] }) }), _jsx("div", { className: "p-8 text-center text-slate-500 text-sm", children: "Not enough daily data to render equity curve. Data appears after at least 2 trading days." })] }));
    }
    const yTicks = [];
    const tickCount = 5;
    for (let i = 0; i < tickCount; i++) {
        yTicks.push(yDomain.min + (i / (tickCount - 1)) * (yDomain.max - yDomain.min));
    }
    const xLabelStep = Math.max(1, Math.floor(allDates.length / 8));
    const xLabels = allDates.filter((_, i) => i % xLabelStep === 0 || i === allDates.length - 1);
    return (_jsxs("div", { className: "bg-surface-dark border border-surface-light rounded-xl overflow-hidden shadow-sm mb-6", children: [_jsxs("div", { className: "p-5 border-b border-surface-light", children: [_jsxs("h3", { className: "font-bold text-white text-lg flex items-center gap-2", children: [_jsx("span", { className: "text-success", children: "\uD83D\uDCC8" }), "Equity Curve"] }), _jsx("p", { className: "text-xs text-slate-400 mt-1", children: "Portfolio value over time across all experiments" })] }), _jsxs("div", { className: "p-5", children: [_jsxs("div", { className: "flex flex-wrap gap-x-4 gap-y-1 mb-4 text-xs", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: "w-3 h-0.5 rounded", style: { backgroundColor: TOTAL_COLOR } }), _jsx("span", { className: "text-slate-300 font-medium", children: "Combined Total" })] }), series.map(s => (_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: "w-3 h-0.5 rounded", style: { backgroundColor: s.color } }), _jsx("span", { className: "text-slate-400", children: s.name })] }, s.id)))] }), _jsx("div", { className: "bg-background-dark rounded-lg border border-surface-light p-3", children: _jsxs("svg", { viewBox: `0 0 ${width} ${height}`, className: "w-full h-auto", onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave, children: [yTicks.map((tick, i) => (_jsxs("g", { children: [_jsx("line", { x1: padding.left, y1: yScale(tick), x2: width - padding.right, y2: yScale(tick), stroke: "#374151", strokeWidth: "0.5", strokeDasharray: "4 2" }), _jsxs("text", { x: padding.left - 8, y: yScale(tick) + 3, textAnchor: "end", fill: "#6b7280", fontSize: "10", children: ["$", Math.round(tick)] })] }, i))), xLabels.map(d => (_jsx("text", { x: xScale(d), y: height - 6, textAnchor: "middle", fill: "#6b7280", fontSize: "9", children: d.slice(5) }, d))), series.map(s => (_jsx("polyline", { points: buildPolyline(s.points), fill: "none", stroke: s.color, strokeWidth: "1.5", strokeLinejoin: "round", strokeLinecap: "round", opacity: 0.7 }, s.id))), _jsx("polyline", { points: buildPolyline(totalSeries.points), fill: "none", stroke: TOTAL_COLOR, strokeWidth: "2.5", strokeLinejoin: "round", strokeLinecap: "round" }), hoveredPoint && (_jsx("line", { x1: hoveredPoint.x, y1: padding.top, x2: hoveredPoint.x, y2: height - padding.bottom, stroke: "#9ca3af", strokeWidth: "0.5", strokeDasharray: "3 3" }))] }) }), hoveredPoint && (_jsxs("div", { className: "mt-3 bg-background-dark border border-surface-light rounded-lg px-4 py-3 text-xs", children: [_jsx("div", { className: "text-slate-400 font-medium mb-2", children: hoveredPoint.date }), _jsx("div", { className: "grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1", children: hoveredPoint.values.map((v, i) => (_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: "w-2 h-2 rounded-full shrink-0", style: { backgroundColor: v.color } }), _jsxs("span", { className: "text-slate-400 truncate", children: [v.name, ":"] }), _jsx("span", { className: "text-slate-200 tabular-nums font-medium", children: formatCurrency(v.value) })] }, i))) })] }))] })] }));
});
//# sourceMappingURL=EquityCurve.js.map